import { useState } from 'react'
import {
  Check,
  Zap,
  Target,
  Shield,
  Globe,
  BarChart3,
  MessageSquare,
  Brain,
  LayoutGrid,
  ArrowRight,
  Users,
  Building2,
  Cpu,
  ShieldCheck,
  Briefcase,
  Megaphone,
} from 'lucide-react'
import BrandLogo from '../components/BrandLogo'

/* ═══════════════════════════════════════════════════════════════════════════
   DIZEE — Investor Deck v3 (/deck3)
   Refined version: deck content + deck2 layout/imagery. Text left, imagery right.
   Problem & Solution slides: scattered real logo assets loaded from /public/logos/deck.
   ═══════════════════════════════════════════════════════════════════════════ */

const DECK_PASSWORD = 'dizee2025'

export default function Deck3Page() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === DECK_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8" style={{ backgroundColor: '#13120A' }}>
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 bg-[#FF2300] rounded-sm flex-shrink-0" />
              <BrandLogo className="h-3 w-auto text-white" />
            </div>
            <p className="text-sm text-zinc-500">Enter the password to view this deck.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-[#FF2300]/50 focus:border-transparent"
              />
              {error && (
                <p className="mt-2 text-xs text-red-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-4 py-3 text-sm font-semibold text-zinc-950 hover:bg-zinc-100 transition-colors"
            >
              View Deck
            </button>
          </form>
          <p className="mt-8 text-[11px] text-zinc-700">
            Confidential — For intended recipients only.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen text-white deck-print-root" style={{ backgroundColor: '#13120A' }}>
      <div className="fixed top-6 right-8 z-50">
        <span className="text-[10px] font-medium text-zinc-800 uppercase tracking-widest">
          Confidential
        </span>
      </div>
      <div className="max-w-[1400px] mx-auto px-8 sm:px-16 lg:px-24">
        {SLIDES.map(({ component: Section }, i) => (
          <section
            key={i}
            className={`deck-slide py-24 sm:py-32 ${i === 0 ? 'min-h-screen flex flex-col justify-center' : ''} ${i < SLIDES.length - 1 ? 'border-b border-white/[0.04]' : 'pb-32 sm:pb-40'} ${i === SLIDES.length - 1 ? 'mb-8' : ''}`}
          >
            <Section />
          </section>
        ))}
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   LAYOUT & SHARED
   ═══════════════════════════════════════════════════════════════════════════ */

interface Slide { title: string; component: React.FC }

function SlideTag({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <span className={`inline-block text-[11px] font-semibold text-[#FF2300] uppercase tracking-widest ${className}`}>
      {children}
    </span>
  )
}

function TwoCol({
  tag,
  headline,
  headlineNode,
  highlight,
  subline,
  children,
  right,
}: {
  tag?: string
  headline?: string
  headlineNode?: React.ReactNode
  highlight?: string
  subline?: string
  children?: React.ReactNode
  right: React.ReactNode
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        {tag && <SlideTag>{tag}</SlideTag>}
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          {headlineNode ?? (<>{(headline ?? '')}{highlight && <span className="text-[#FF2300]">{highlight}</span>}</>)}
        </h2>
        {subline && <p className="mt-4 text-base sm:text-lg text-zinc-400 break-words">{subline}</p>}
        <div className="min-w-0">{children}</div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[340px] min-w-0">
        {right}
      </div>
    </div>
  )
}

function GrayBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-white/[0.08] bg-zinc-800/40 p-5 min-w-0">
      <h3 className="text-[15px] font-semibold text-white mb-2">{title}</h3>
      <p className="text-[14px] text-zinc-400 leading-relaxed break-words">{children}</p>
    </div>
  )
}

/* Problem slide & Solution slide: same deck logos, floating with label underneath. */
const PROBLEM_LOGOS: { label: string; src: string }[] = [
  { label: 'iMessage', src: '/logos/deck/apple.svg' },
  { label: 'Asana', src: '/logos/deck/asana.svg' },
  { label: 'ASCAP', src: '/logos/deck/ascap.svg' },
  { label: 'Gmail', src: '/logos/deck/gmail.svg' },
  { label: 'Google Sheets', src: '/logos/deck/googlesheets.svg' },
  { label: 'MasterTour', src: '/logos/deck/mastertour.svg' },
  { label: 'Slack', src: '/logos/deck/slack.svg' },
  { label: 'Spotify', src: '/logos/deck/spotify.svg' },
]

function MockFragmentedLogos() {
  const items: {
    left: string
    top: string
    rotation?: number
    opacity?: number
    translateX?: number
    translateY?: number
    zIndex?: number
  }[] = [
    { left: '24%', top: '6%', rotation: -5, opacity: 0.94, translateX: 4, translateY: -2, zIndex: 10 },
    { left: '68%', top: '8%', rotation: 4, opacity: 1, translateX: -3, translateY: 5, zIndex: 8 },
    { left: '75%', top: '38%', rotation: -2, opacity: 0.9, translateX: 6, translateY: 2, zIndex: 12 },
    { left: '22%', top: '32%', rotation: 3, opacity: 0.97, translateX: -5, translateY: -4, zIndex: 9 },
    { left: '44%', top: '26%', rotation: -4, opacity: 0.92, translateX: 2, translateY: 6, zIndex: 11 },
    { left: '14%', top: '58%', rotation: 2, opacity: 0.98, translateX: -2, translateY: -3, zIndex: 13 },
    { left: '48%', top: '62%', rotation: -3, opacity: 0.93, translateX: 5, translateY: 1, zIndex: 7 },
    { left: '72%', top: '68%', rotation: 5, opacity: 0.96, translateX: -4, translateY: 4, zIndex: 14 },
  ]
  return (
    <div className="relative w-full max-w-lg h-[380px]">
      {PROBLEM_LOGOS.map((logo, i) => {
        const item = items[i]
        const tx = item.translateX ?? 0
        const ty = item.translateY ?? 0
        return (
          <div
            key={i}
            className="absolute flex flex-col items-center gap-2"
            style={{
              left: item.left,
              top: item.top,
              zIndex: item.zIndex ?? 10,
              opacity: item.opacity ?? 1,
              transform: `translate(${tx}px, ${ty}px) rotate(${item.rotation ?? 0}deg)`,
            }}
          >
            <img src={logo.src} alt={logo.label} className="h-12 w-auto object-contain" />
            <span className="text-[12px] text-zinc-500 whitespace-nowrap">{logo.label}</span>
          </div>
        )
      })}
    </div>
  )
}

/* Solution slide: tools + people (agent, promoter, managers) all connecting to center. */
const SOLUTION_ITEMS: (
  | { type: 'logo'; label: string; src: string }
  | { type: 'role'; label: string; Icon: React.ComponentType<{ className?: string }> }
)[] = [
  ...PROBLEM_LOGOS.map((l) => ({ type: 'logo' as const, label: l.label, src: l.src })),
  { type: 'role', label: 'Agent', Icon: Briefcase },
  { type: 'role', label: 'Promoter', Icon: Megaphone },
  { type: 'role', label: 'Managers', Icon: Users },
]

function MockCentralDizzy() {
  const positions = [
    { left: '2%', top: '14%' },   { left: '18%', top: '2%' },   { left: '46%', top: '2%' },   { left: '74%', top: '2%' },
    { left: '90%', top: '20%' },  { left: '90%', top: '46%' },  { left: '90%', top: '72%' },
    { left: '74%', top: '90%' },  { left: '46%', top: '90%' },
    { left: '2%', top: '48%' },   { left: '2%', top: '88%' },
  ]
  return (
    <div className="relative w-full max-w-lg h-[380px]">
      {SOLUTION_ITEMS.map((item, i) => (
        <div
          key={i}
          className="absolute flex flex-col items-center gap-2 z-10"
          style={{ left: positions[i].left, top: positions[i].top }}
        >
          {item.type === 'logo' ? (
            <img src={item.src} alt={item.label} className="h-12 w-auto object-contain" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-zinc-400">
              <item.Icon className="w-6 h-6" />
            </div>
          )}
          <span className="text-[12px] text-zinc-500 whitespace-nowrap">{item.label}</span>
        </div>
      ))}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden>
        {positions.map((pos, i) => {
          const cx = parseFloat(pos.left) + 6
          const cy = parseFloat(pos.top) + 4
          return (
            <line key={i} x1={cx} y1={cy} x2={50} y2={50} stroke="rgba(255,255,255,0.25)" strokeWidth="0.3" strokeDasharray="1 1" />
          )
        })}
      </svg>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center gap-2 z-10">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
    </div>
  )
}

/* ═══════════════════════════════════════════════════════════════════════════
   SLIDES
   ═══════════════════════════════════════════════════════════════════════════ */

function SlideTitle() {
  return (
    <div>
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <div className="flex items-center gap-3 mt-6">
        <span className="inline-flex items-center rounded-full border border-[#FF2300]/30 bg-[#FF2300]/10 px-4 py-1.5 text-[12px] font-medium text-[#FF2300]">Pre-Seed</span>
        <span className="text-[13px] text-zinc-500">February 2026</span>
      </div>
      <h2 className="mt-10 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] max-w-4xl">
        The new{' '}
        <span className="text-[#FF2300]">operating system</span>
        {' '}and{' '}
        <span className="text-[#FF2300]">source of truth</span>
        {' '}for modern music careers.
      </h2>
      <p className="mt-6 text-base sm:text-lg text-zinc-400 leading-relaxed max-w-2xl">
        So artists, managers, and their teams stay aligned around a single source of truth.
      </p>
      <p className="text-sm text-zinc-500 mt-12">www.dizee.com</p>
    </div>
  )
}

function SlideWhatsHappening() {
  return (
    <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-w-0">
      <div className="min-w-0">
        <SlideTag>What&apos;s Happening Now</SlideTag>
        <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white leading-[1.08] break-words">
          The industry has shifted.{' '}
          <span className="text-[#FF2300]">Fundamentally.</span>
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-4 min-w-0">
          <GrayBox title="A new landscape">The music industry is undergoing a seismic technological and cultural transformation.</GrayBox>
          <GrayBox title="Decentralized Power">Labels and traditional gatekeepers no longer manage most careers.</GrayBox>
          <GrayBox title="Independent Era">Independent artists, managers, and small teams now run their own businesses.</GrayBox>
          <GrayBox title="Infrastructure Gap">Responsibility moved to artists and teams, but the operating infrastructure never followed.</GrayBox>
        </div>
      </div>
      <div className="flex items-center justify-center min-h-[280px] lg:min-h-[360px] w-full">
        <div className="relative w-full max-w-lg rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
          <img
            src="/deck/industry-shifted.png"
            alt="The industry has shifted — burning buildings, independents rising"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>
      </div>
    </div>
  )
}

function SlideIndustryTrends() {
  return (
    <TwoCol
      tag="Industry Trends"
      headline="The industry has changed. The tools haven't."
      subline="Here's what's coming next."
      right={
        <div className="flex flex-col items-center gap-4 w-full max-w-lg">
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
            <img
              src="/deck/industry-wins.png"
              alt="Creators on top — independent power rising"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 w-full">
            <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="rounded-xl border border-[#FF2300]/25 bg-[#FF2300]/[0.08] p-4 hover:bg-[#FF2300]/[0.12] transition-colors">
              <p className="text-3xl font-bold text-[#FF2300]">75.9M</p>
              <p className="text-[12px] text-zinc-400 mt-0.5">Music Creators (2023)</p>
              <p className="text-[10px] text-zinc-600 mt-1.5">Source</p>
            </a>
            <a href="https://routenote.com/blog/nearly-200-million-music-creators-predicted-by-2030/" target="_blank" rel="noopener noreferrer" className="rounded-xl border border-emerald-500/25 bg-emerald-500/[0.08] p-4 hover:bg-emerald-500/[0.12] transition-colors">
              <p className="text-3xl font-bold text-emerald-400">198.2M</p>
              <p className="text-[12px] text-zinc-400 mt-0.5">By 2030</p>
              <p className="text-[10px] text-zinc-600 mt-1.5">Source</p>
            </a>
          </div>
        </div>
      }
    >
      <div className="mt-8 space-y-4">
        <GrayBox title="New AI Tools">Gen AI music & audiovisual market could rise from €3B to €64B by 2028.</GrayBox>
        <GrayBox title="Barrier to Entry">120,000 new tracks hit streaming services each day (Q1 2023).</GrayBox>
        <GrayBox title="Rise of the Creators">Creator Economy: $252.33B (2025).</GrayBox>
        <GrayBox title="Fragmented Ecosystem">Performance rights $2.9B (2024) — another revenue stream to manage.</GrayBox>
      </div>
    </TwoCol>
  )
}

function SlideProblemCurrentTools() {
  return (
    <TwoCol
      tag="The Problem — Current State"
      headline="Music business is still "
      highlight="outdated."
      subline="Manual and disconnected. No unified system to manage the modern music business."
      right={<MockFragmentedLogos />}
    >
      <div className="mt-8 grid sm:grid-cols-2 gap-4 min-w-0">
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Zap className="w-4 h-4 text-zinc-500 shrink-0" /><span className="text-sm font-semibold text-white break-words">Manual Processes</span></div>
          <p className="text-[13px] text-zinc-400 break-words">Nothing automated or connected.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 min-w-0">
          <div className="flex items-center gap-2 mb-2"><MessageSquare className="w-4 h-4 text-zinc-500 shrink-0" /><span className="text-sm font-semibold text-white break-words">Fragmented Communication</span></div>
          <p className="text-[13px] text-zinc-400 break-words">Spreadsheets, texts, email — critical decisions in iMessage nobody can search.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Shield className="w-4 h-4 text-zinc-500 shrink-0" /><span className="text-sm font-semibold text-white break-words">Legacy Tools</span></div>
          <p className="text-[13px] text-zinc-400 break-words">Built for a different era. Can&apos;t keep up.</p>
        </div>
        <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-5 min-w-0">
          <div className="flex items-center gap-2 mb-2"><Globe className="w-4 h-4 text-zinc-500 shrink-0" /><span className="text-sm font-semibold text-white break-words">Disconnected Systems</span></div>
          <p className="text-[13px] text-zinc-400 break-words">Data in a dozen platforms. No way to connect the dots.</p>
        </div>
      </div>
    </TwoCol>
  )
}

function SlideProblemHighLevel() {
  return (
    <div>
      <SlideTag>The Problem</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Artists lack real-time visibility{' '}
        <span className="text-[#FF2300]">into what&apos;s happening in their career.</span>
      </h2>
      <div className="mt-12 max-w-3xl space-y-6">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Managers don&apos;t have a centralized system, the time, or reliable access to data needed to make informed decisions.</p>
        </div>
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Today&apos;s tools are slow, fragmented, and static — easy to miss moments that matter. By the time data is gathered, the opportunity has passed.</p>
        </div>
        <div className="flex items-start gap-3">
          <MessageSquare className="w-5 h-5 text-red-400/60 shrink-0 mt-0.5" />
          <p className="text-base text-zinc-300">Communication is chaotic — iMessage, email, text threads, DMs. Nothing is tracked, searchable, or connected.</p>
        </div>
      </div>
    </div>
  )
}

const REAL_CONVOS_BEFORE_AFTER = [
  {
    question: 'Do we have an updated ticket count for the NY show?',
    before: [
      { from: 'Mngr', msg: "I don't see it in the email, let me ask the agents." },
      { from: 'Mngr', msg: 'Ok they responded and hit the promoter to get it.' },
      { from: 'Mngr', msg: "Next day; Ok promoter sent back to them, it's 583 so 70% sold." },
    ],
    after: [
      { from: 'Mngr', msg: 'ya all the counts are in dizee, you can check it anytime and see the latest counts.' },
      { from: 'Artist', msg: "oh ok sick, so easy, now I dont have to ask you everyday haha" },
    ],
  },
  {
    question: 'How are sales looking for the LA show, are we pacing good?',
    before: [],
    after: [
      { from: 'Mngr', msg: "Ya in dizee you can actually see how all the shows are pacing and compare it to the last time you played there." },
      { from: 'Artist', msg: "Oh wow, that's so helpful!" },
    ],
  },
  {
    question: 'Did the Chicago show get confirmed?',
    before: [
      { from: 'Mngr', msg: "I think so but let me ask, the agents never confirmed." },
    ],
    after: [
      { from: 'Mngr', msg: 'I got the notification that Chicago got confirmed, stocked for that one' },
    ],
  },
]
/* Scattered one-off messages for the left column (not a thread). */
const SCATTERED_BUBBLES = [
  { from: 'Artist', msg: "What's my flight for this weekend?" },
  { from: 'Manager', msg: 'Did we get the splits for the remix?' },
  { from: 'Agent', msg: 'Need to confirm Denver by EOD' },
  { from: 'Artist', msg: 'When do we get paid for the festival?' },
  { from: 'Manager', msg: 'Which spreadsheet has the routing?' },
  { from: 'Label', msg: 'Final master + metadata for the single?' },
]
function SlideExampleMessages() {
  const conv = REAL_CONVOS_BEFORE_AFTER[0]
  return (
    <div>
      <SlideTag>Real Conversations</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-tighter text-white">
        Before DIZEE vs after DIZEE.
      </h2>
      {/* Centered artist question at top, highlighted */}
      <div className="mt-10 flex justify-center">
        <div className="rounded-2xl rounded-bl-sm px-5 py-3 max-w-md bg-[#FF2300]/15 border border-[#FF2300]/30 text-center">
          <p className="text-[11px] font-semibold text-[#FF2300] uppercase tracking-wider mb-1">Artist</p>
          <p className="text-[14px] text-white">&ldquo;{conv.question}&rdquo;</p>
        </div>
      </div>
      {/* Left: scattered text bubbles. Right: Before (top) + After (bottom) conversations */}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-none">
        <div className="space-y-2">
          <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Scattered asks — no single place to check</p>
          <div className="flex flex-col gap-2">
            {SCATTERED_BUBBLES.map((m, i) => (
              <div
                key={i}
                className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full max-w-[85%] bg-white/5 border border-white/10 backdrop-blur-sm"
                style={{ marginLeft: i % 3 === 0 ? '0' : i % 3 === 1 ? '8%' : '4%' }}
              >
                <span className="text-[11px] font-semibold text-zinc-500">{m.from}</span>
                <p className="text-[13px] text-zinc-400 mt-0.5">&ldquo;{m.msg}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
        <div className="space-y-6">
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-red-400/90 uppercase tracking-wider">Before DIZEE</p>
            {conv.before.length > 0 ? conv.before.map((m, i) => (
              <div key={i} className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-red-500/10 border border-red-500/20 backdrop-blur-md">
                <span className="text-[11px] font-semibold text-red-400/90">{m.from}</span>
                <p className="text-[13px] text-zinc-300 mt-0.5">&ldquo;{m.msg}&rdquo;</p>
              </div>
            )) : (
              <div className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-red-500/10 border border-red-500/20 backdrop-blur-md">
                <p className="text-[12px] text-zinc-500 italic">No single place to check — back-and-forth across email and agents.</p>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <p className="text-[11px] font-semibold text-emerald-400/90 uppercase tracking-wider">After DIZEE</p>
            {conv.after.map((m, i) => (
              <div key={i} className="rounded-2xl rounded-bl-sm px-4 py-2.5 w-full min-w-0 bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-md">
                <span className="text-[11px] font-semibold text-emerald-400/90">{m.from}</span>
                <p className="text-[13px] text-zinc-300 mt-0.5">&ldquo;{m.msg}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function SlideCentralizes() {
  return (
    <TwoCol
      tag="The Solution"
      headlineNode={
        <>
          <span className="text-[#FF2300]">DIZEE</span>
          {' '}centralizes the{' '}
          <br />
          modern music career
        </>
      }
      subline={undefined}
      right={<MockCentralDizzy />}
    >
      <>
        <p className="mt-4 text-base sm:text-lg text-zinc-400">
          Into a single{' '}
          <span className="text-[#FF2300] font-semibold">operating system</span>
          {' '}and{' '}
          <span className="text-[#FF2300] font-semibold">source of truth.</span>
        </p>
        <hr className="mt-4 border-white/10" />
      </>
      <div className="mt-8 grid sm:grid-cols-1 gap-4">
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <BarChart3 className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Real-Time Clarity</h3>
          <p className="text-[14px] text-zinc-400">Unified view of releases, shows, finances, team — in one place.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Brain className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">Smarter Decisions</h3>
          <p className="text-[14px] text-zinc-400">AI surfaces opportunities as they happen so you can act, not react.</p>
        </div>
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5">
          <Target className="w-5 h-5 text-[#FF2300] mb-2" />
          <h3 className="text-base font-semibold text-white mb-1">End-to-End Management</h3>
          <p className="text-[14px] text-zinc-400">Catalog, touring, team, projects — all connected.</p>
        </div>
      </div>
      <p className="mt-8 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-zinc-300 italic text-left leading-snug">
        Anyone can now manage a modern music career end-to-end with{' '}
        <span className="text-white">DIZEE</span>
      </p>
    </TwoCol>
  )
}

const PRODUCT_ITEMS: { title: string; desc: string; mock: React.ReactNode }[] = [
  {
    title: 'Dashboard',
    desc: 'Real-time view of your entire music career in one place',
    mock: (
      <div className="w-full text-left p-2 space-y-1.5 min-h-[5.5rem] flex flex-col justify-center">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span className="text-[#FF2300] font-medium">Shows</span><span>Schedule</span><span>Tasks</span></div>
        <div className="grid grid-cols-2 gap-1"><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">42</span><span className="text-[10px] block text-zinc-500">Shows</span></div><div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-sm font-semibold text-white">53</span><span className="text-[10px] block text-zinc-500">Tasks</span></div></div>
      </div>
    ),
  },
  {
    title: 'Teams',
    desc: 'Connect team members and streamline communication',
    mock: (
      <div className="w-full text-left p-2 space-y-1 min-h-[5.5rem] flex flex-col justify-center">
        {[{ name: 'Charly Manager', initials: 'CM' }, { name: 'Promoter', initials: 'P' }].map((m, i) => (
          <div key={i} className="flex items-center gap-2 py-1 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2">
            <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center shrink-0">
              <span className="text-[9px] font-semibold text-zinc-400">{m.initials}</span>
            </div>
            <span className="text-[11px] text-zinc-400">{m.name}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: 'Bookings',
    desc: 'Ticket counts, guarantee tracking, promoter CRM',
    mock: (
      <div className="w-full text-left p-2 min-h-[5.5rem] flex flex-col justify-center">
        <div className="text-[11px] text-zinc-500 mb-1 py-1.5">Tempo · Cap 400 · Sold 328</div>
        <div className="h-1.5 rounded-full bg-white/10 backdrop-blur-sm"><div className="h-full rounded-full bg-[#FF2300] w-[82%]" /></div>
        <div className="text-[10px] text-[#FF2300] mt-0.5">82%</div>
      </div>
    ),
  },
  {
    title: 'Music',
    desc: 'Discography, metadata, splits, credits',
    mock: (
      <div className="w-full text-left p-2 space-y-1 min-h-[5.5rem] flex flex-col justify-center">
        <div className="text-[10px] text-zinc-500 border-b border-white/10 pb-1">Releases · Splits</div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Single v2 · Splits 50/50</span></div>
      </div>
    ),
  },
  {
    title: 'Tour Manager',
    desc: 'Touring operations, travel, communication',
    mock: (
      <div className="w-full text-left p-2 space-y-1 min-h-[5.5rem] flex flex-col justify-center">
        <div className="flex gap-2 text-[10px] text-zinc-500 border-b border-white/10 pb-1"><span>Routing</span><span>Travel</span><span>Crew</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Denver → LA → Chicago</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5"><span className="text-[11px] text-zinc-400">Crew ETA 4:30 PM</span></div>
      </div>
    ),
  },
  {
    title: 'Tasks',
    desc: 'Tasks, notes, files, collaboration',
    mock: (
      <div className="w-full text-left p-2.5 space-y-1.5 min-h-[6rem] flex flex-col justify-center">
        <div className="flex gap-2 text-[10px] text-zinc-500 py-1"><span className="text-[#FF2300] font-medium">3</span> due today</div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-[#FF2300]" /><span className="text-[11px] text-zinc-400">Finalize splits</span></div>
        <div className="rounded bg-white/5 backdrop-blur-sm border border-white/10 p-1.5 flex items-center gap-2"><div className="w-1.5 h-1.5 rounded-full bg-zinc-500" /><span className="text-[11px] text-zinc-400">Send artwork</span></div>
      </div>
    ),
  },
  {
    title: 'AI Intelligence',
    desc: 'At-risk shows, pacing vs last time, bonuses. Factual insights in context.',
    mock: (
      <div className="w-full text-left p-3 space-y-2 min-h-[5.5rem] flex flex-col justify-center">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium border-b border-white/10 pb-1.5">Insights</div>
        <div className="rounded-lg bg-white/5 backdrop-blur-sm border border-white/10 px-2.5 py-2 space-y-1.5">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
            <span className="text-[11px] text-zinc-300">LA pacing 20% slower than last LA</span>
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-zinc-500">990 sold</span>
            <span className="text-[#FF2300] font-medium">$2K bonus at 1,000</span>
          </div>
          <div className="h-1 rounded-full bg-white/10"><div className="h-full rounded-full bg-[#FF2300]/80 w-[99%]" /></div>
        </div>
      </div>
    ),
  },
  {
    title: '24/7 Sync',
    desc: 'Google Sheets, email, platform integrations',
    mock: (
      <div className="w-full text-left p-3 space-y-2 min-h-[5.5rem] flex flex-col justify-center">
        <div className="space-y-1.5">
          {[
            { label: 'Google Sheets', status: 'Live', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Email', status: 'Synced', color: 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' },
            { label: 'Last sync', status: '2m ago', color: 'bg-white/5 border-white/10 text-zinc-500' },
          ].map((row, i) => (
            <div key={i} className={`rounded-lg border px-2 py-1.5 flex items-center justify-between text-[11px] backdrop-blur-sm ${row.color}`}>
              <span className="text-zinc-300">{row.label}</span>
              <span className="font-medium">{row.status}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
]
function SlideProduct() {
  return (
    <div>
      <SlideTag>Product</SlideTag>
      <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tighter text-white">
        A unified platform that centralizes your career.
      </h2>
      <p className="mt-2 text-zinc-400">Dashboard, Teams, Bookings, Music, Tour Manager, Tasks, AI Intelligence, 24/7 Sync.</p>

      {/* Big forward-facing product overview image — 30% smaller */}
      <div className="mt-8 w-[70%] mx-auto">
        <img
          src="/deck/dizee-overview.png"
          alt="DIZEE product overview"
          className="w-full h-auto object-contain"
        />
      </div>

      {/* Product areas below — consistent spacing, aligned box sizing, 2-line text */}
      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {PRODUCT_ITEMS.map((item, i) => (
          <div key={i} className="flex flex-col gap-2 min-h-[200px]">
            <h3 className="text-[15px] font-semibold text-white min-h-[2rem] flex items-center">{item.title}</h3>
            <p className="text-[12px] text-zinc-400 min-h-[2.5rem] line-clamp-2 leading-snug">{item.desc}</p>
            <div className="mt-1 rounded-lg bg-white/[0.04] border border-white/[0.06] p-2.5 min-h-[5.5rem] flex items-center justify-center">
              {item.mock}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideMarketSize() {
  return (
    <TwoCol
      tag="Market Size"
      headline="A massive, underserved market "
      highlight="growing every year."
      right={
        <div className="flex flex-col items-center gap-4 w-full max-w-lg">
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
            <img
              src="/deck/crowd.png"
              alt="Market size — audience and creators"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      }
    >
      <div className="mt-8 grid grid-cols-1 gap-6">
        {[
          { label: 'TAM — Creator Infrastructure Software', stat: '$7.5B', sub: '3% of $252B Global Creator Economy (2025)', color: 'border-[#FF2300]/20 bg-[#FF2300]/[0.04]' },
          { label: 'SAM — Music (Initial Wedge)', stat: '$750M', sub: '~13M artists tracked globally (2025)', color: 'border-violet-500/20 bg-violet-500/[0.04]' },
          { label: 'SOM — Professional Artist Teams', stat: '$15–20M', sub: 'ARR · Managed / team-operated artists', color: 'border-emerald-500/20 bg-emerald-500/[0.04]' },
        ].map((s, i) => (
          <div key={i} className={`rounded-xl border ${s.color} p-6`}>
            <h3 className="text-[15px] font-semibold text-white mb-1">{s.label}</h3>
            <p className="text-3xl font-bold text-white">{s.stat}</p>
            <p className="text-[12px] text-zinc-500 mt-1">{s.sub}</p>
          </div>
        ))}
      </div>
    </TwoCol>
  )
}

function SlideBusinessModel() {
  const tiers = [
    {
      tier: 'Artist',
      subtitle: 'Starter Plan',
      price: '$45',
      note: '/mo',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: undefined as string[] | undefined,
      popular: false,
    },
    {
      tier: 'Artist Pro',
      price: '$75',
      note: '/mo',
      audience: 'Single user',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync'],
      plusFeatures: ['Ticket Sales Tracker', 'Tour Manager', 'Dizee AI Assistant', 'Finance Tracking'],
      popular: true,
    },
    {
      tier: 'Enterprise',
      price: '$125',
      note: '/mo',
      audience: '2+ users ($15 per additional seat)',
      features: ['Dashboard', 'Catalog & Release Management', 'Career Assistant', 'Schedule & Calendar', 'Tasks', '24/7 Sync', 'Tour Manager', 'Finance Tracking', 'Ticket Sales Tracker', 'Dizee AI Assistant'],
      plusFeatures: ['Teams', 'Songwriter Credits', 'Dedicated Customer Support'],
      popular: false,
    },
  ]
  return (
    <div>
      <SlideTag>Business Model</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Simple subscription.{' '}
        <span className="text-[#FF2300]">Predictable revenue.</span>
      </h2>
      <div className="mt-10 grid lg:grid-cols-3 gap-4">
        {tiers.map((p, i) => (
          <div key={i} className={`rounded-xl border p-5 flex flex-col ${p.popular ? 'border-[#FF2300]/30 bg-[#FF2300]/[0.06] ring-1 ring-[#FF2300]/20' : 'border-white/[0.06] bg-white/[0.02]'}`}>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-white">{p.tier}</h3>
              {p.popular && (
                <span className="text-[9px] font-semibold text-[#FF2300] bg-[#FF2300]/10 rounded-full px-2 py-0.5">Popular</span>
              )}
            </div>
            {p.subtitle && <p className="text-[11px] text-zinc-600 mb-2">{p.subtitle}</p>}
            <p className="text-2xl font-bold text-white mt-1">{p.price}<span className="text-[12px] font-normal text-zinc-500 ml-1">{p.note}</span></p>
            <p className="text-[11px] text-[#FF2300] font-medium mt-1">{p.audience}</p>
            <ul className="mt-4 space-y-1.5">
              {p.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2">
                  <Check className="w-3.5 h-3.5 mt-0.5 text-zinc-600 shrink-0" />
                  <span className="text-[12px] text-zinc-400">{f}</span>
                </li>
              ))}
            </ul>
            {p.plusFeatures && p.plusFeatures.length > 0 && (
              <>
                <div className="my-3 border-t border-white/[0.06]" />
                <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-widest mb-2">Plus</p>
                <ul className="space-y-1.5">
                  {p.plusFeatures.map((f, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-[#FF2300] shrink-0" />
                      <span className="text-[12px] text-zinc-300">{f}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideGTM() {
  const phases = [
    { phase: 'Phase 1', title: 'Founder-Led Wedge', timeline: 'Months 0–6', target: 'First 200 accounts', items: ['Direct outreach to independent managers', '1:1 onboarding', 'Early product feedback'] },
    { phase: 'Phase 2', title: 'Signal Amplification', timeline: 'Months 6–12', target: '500–700 accounts', items: ['Case studies', 'Referral incentives', 'Partnerships', 'Testimonials'] },
    { phase: 'Phase 3', title: 'Scalable Acquisition', timeline: 'Months 12–24', target: '1,500+ accounts', items: ['Content + education', 'Workflow partnerships', 'Referral engine', 'API integrations'] },
  ]
  return (
    <div>
      <SlideTag>Go-To-Market</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Three phases.{' '}
        <span className="text-[#FF2300]">Founder-led to scalable.</span>
      </h2>
      <div className="mt-12 flex flex-col lg:flex-row lg:items-stretch gap-4 lg:gap-2">
        {phases.map((p, i) => (
          <div key={i} className="flex items-stretch gap-2 flex-1">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 flex-1">
              <span className="text-base sm:text-lg font-bold text-[#FF2300]">{p.phase}</span>
              <h3 className="text-[16px] font-semibold text-white mt-2">{p.title}</h3>
              <p className="text-[11px] text-zinc-600">{p.timeline} · {p.target}</p>
              <ul className="mt-4 space-y-2">
                {p.items.map((item, j) => (
                  <li key={j} className="flex items-start gap-2">
                    <div className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#FF2300] shrink-0" />
                    <span className="text-[13px] text-zinc-400">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            {i < phases.length - 1 && (
              <div className="hidden lg:flex items-center justify-center flex-shrink-0 px-1">
                <ArrowRight className="w-6 h-6 text-[#FF2300]/50" />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideCompetition() {
  /* X: Static Software (left) → 360° Career Management (right). Y: Disconnected Workflows (bottom) → Intelligent Operating System (top).
     Positions based on: music/creator focus (X) and connected/intelligent system vs point tools (Y). */
  const competitors: { name: string; x: number; y: number; highlight?: boolean }[] = [
    { name: 'Slack', x: 18, y: 38 },           // Messaging/collab; not music; fragmented workflows
    { name: 'Monday', x: 22, y: 48 },          // Generic work management; dashboards, automations
    { name: 'Asana', x: 32, y: 58 },           // Generic project/task management; AI assists
    { name: 'Notion', x: 42, y: 62 },          // Flexible wiki/docs; unified workspace, not music-specific
    { name: 'IndieFlow', x: 52, y: 48 },       // Music: distribution, projects, royalties; single dashboard, not full OS
    { name: 'Venice Music', x: 62, y: 70 },    // Music: strategy, distribution, Co-Manager AI, education
    { name: 'Artist Growth', x: 74, y: 80 },   // Music biz: roster, events, AG Intelligence, touring; used by majors
    { name: 'DIZEE', x: 90, y: 92, highlight: true },
  ]
  return (
    <div>
      <SlideTag>Competition</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        From static software{' '}
        <span className="text-[#FF2300]">to an intelligent operating system.</span>
      </h2>
      <div className="mt-10 relative w-full max-w-[700px] mx-auto">
        <svg viewBox="0 0 600 520" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
          <line x1="300" y1="40" x2="300" y2="500" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <line x1="20" y1="270" x2="580" y2="270" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" />
          <text x="20" y="265" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600"><tspan fill="#FF2300">X: </tspan><tspan fill="white">Static Software</tspan></text>
          <text x="580" y="265" textAnchor="end" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">360° Career Management</text>
          <text x="300" y="28" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600"><tspan fill="#FF2300">Y: </tspan><tspan fill="white">Intelligent Operating System</tspan></text>
          <text x="300" y="512" textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="11" fontWeight="600">Disconnected Workflows</text>
          {competitors.map((c, i) => {
            const cx = 20 + (c.x / 100) * 560
            const cy = 500 - (c.y / 100) * 460
            return (
              <g key={i}>
                <circle cx={cx} cy={cy} r={c.highlight ? 14 : 8} fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.8)'} />
                <text x={cx} y={cy + (c.highlight ? 28 : 22)} textAnchor="middle" fill={c.highlight ? '#FF2300' : 'rgba(255,255,255,0.6)'} fontSize={c.highlight ? '14' : '12'} fontWeight={c.highlight ? '700' : '600'}>{c.name}</text>
              </g>
            )
          })}
        </svg>
      </div>
    </div>
  )
}

function SlideAdvantages() {
  const bullets = [
    'Built by a 2x Grammy-nominated manager — 2B+ streams. Deep industry understanding.',
    'CEO with proven credibility and active Billboard-charting label.',
    'Rare hybrid team: music insiders + modern product builders.',
    'DIZEE AI informed by 15+ years lived experience — music-native intelligence.',
    'First-mover in a new category. Defining the space.',
  ]
  return (
    <TwoCol
      tag="Why DIZEE Wins"
      headline="Why DIZEE wins."
      right={
        <div className="flex flex-col items-center gap-4 w-full max-w-lg">
          <div className="relative w-full rounded-2xl overflow-hidden border border-white/[0.08] shadow-xl aspect-[16/10]">
            <img
              src="/deck/party.png"
              alt="Why DIZEE wins"
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </div>
        </div>
      }
    >
      <ul className="mt-8 space-y-4 max-w-3xl">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3">
            <Check className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <span className="text-[15px] text-zinc-300">{b}</span>
          </li>
        ))}
      </ul>
    </TwoCol>
  )
}

const TEAM_MEMBERS = [
  {
    name: 'Ryan Thomas',
    role: 'CEO',
    points: [
      '15+ years in the dance music industry',
      'Managed Grammy-nominated and platinum-selling artists',
      'Deep global relationships with agencies, promoters, and labels',
    ],
  },
  {
    name: 'Edward Contreras',
    role: 'CTO',
    points: [
      'Technologist, product builder, and entrepreneur',
      'Expertise in AI systems, music technology, and multi-product ecosystems',
      "Architect behind DIZEE's platform and integration layer",
    ],
  },
  {
    name: 'Aaron P. Wong',
    role: 'COO',
    points: [
      'Founder of Super73, a globally recognized electric bike company',
      'Experienced in scaling hardware businesses and social presence',
      'Raised and closed multiple rounds of venture funding',
    ],
  },
  {
    name: 'Owais Ghaus',
    role: 'CMO',
    points: [
      'Artist, marketer, and technologist',
      'Music executive with deep industry connections',
      'Specializes in content strategy, creator marketing, and artist development',
    ],
  },
  {
    name: 'Christopher Alba',
    role: 'CBO',
    points: [
      'Business development and strategic partnerships',
      'Deep network across music and entertainment verticals',
      'Focused on enterprise and label partnerships',
    ],
  },
]
function TeamCard({ t }: { t: (typeof TEAM_MEMBERS)[0] }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 flex flex-col min-h-[200px]">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF2300]/30 to-[#FF4500]/20 border border-white/[0.08] flex items-center justify-center flex-shrink-0">
          <span className="text-[13px] font-semibold text-white">{t.name.split(' ').map((n) => n[0]).join('')}</span>
        </div>
        <div className="min-w-0">
          <h3 className="text-[14px] font-semibold text-white">{t.name}</h3>
          <p className="text-[11px] text-[#FF2300] font-medium">{t.role}</p>
        </div>
      </div>
      <ul className="space-y-2 flex-1">
        {t.points.map((point, j) => (
          <li key={j} className="flex items-start gap-2">
            <div className="mt-1.5 h-1 w-1 rounded-full bg-zinc-600 shrink-0" />
            <p className="text-[12px] text-zinc-400">{point}</p>
          </li>
        ))}
      </ul>
    </div>
  )
}
function SlideTeam() {
  const firstRow = TEAM_MEMBERS.slice(0, 3)
  const secondRow = TEAM_MEMBERS.slice(3, 5) // Owais, Christopher
  return (
    <div>
      <SlideTag>Team</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
        The team building this.
      </h2>
      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        {firstRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 lg:max-w-[66.666%] mx-auto">
        {secondRow.map((t, i) => (
          <TeamCard key={i} t={t} />
        ))}
      </div>
    </div>
  )
}

function SlideAsk() {
  const uses = [
    { label: 'Product–market fit with real artists and managers', icon: Zap },
    { label: 'Core platform development', icon: Cpu },
    { label: 'Deep integrations', icon: LayoutGrid },
    { label: 'Marquee artist onboarding', icon: Users },
    { label: 'Founding team & execution', icon: Building2 },
    { label: 'Infrastructure & compliance', icon: ShieldCheck },
  ]
  return (
    <div>
      <SlideTag>The Ask</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        Raising <span className="text-[#FF2300]">$2.5M</span> to accelerate.
      </h2>
      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {uses.map((u, i) => (
          <div key={i} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5 flex items-start gap-3">
            <u.icon className="w-5 h-5 text-[#FF2300] shrink-0 mt-0.5" />
            <p className="text-[14px] text-zinc-300">{u.label}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function SlideClosingStatement() {
  return (
    <div className="flex flex-col justify-center items-start text-left min-h-[60vh] py-16">
      <p className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-semibold tracking-tight text-white leading-tight max-w-4xl">
        DIZEE is building the modern operating system the music industry has been missing.
      </p>
      <p className="mt-8 text-xl sm:text-2xl lg:text-3xl font-medium tracking-tight text-zinc-300 leading-snug max-w-3xl">
        The team is built. The product is in motion. Now it&apos;s about capturing the market.
      </p>
    </div>
  )
}

function SlideClosing() {
  return (
    <div className="flex flex-col justify-center py-12">
      <div className="flex items-center gap-2.5 mb-8">
        <div className="w-6 h-6 bg-[#FF2300] rounded-sm flex-shrink-0" />
        <BrandLogo className="h-4 w-auto text-white" />
      </div>
      <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-300 leading-snug max-w-2xl">
        The new operating system and source of truth for modern music careers.
      </p>
      <div className="mt-12 space-y-2">
        <p className="text-[14px] text-[#FF2300] font-medium">invest@dizee.com</p>
        <p className="text-[13px] text-zinc-500">www.dizee.com</p>
      </div>
    </div>
  )
}

function SlideAppendix() {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <p className="text-[11px] font-semibold text-zinc-700 uppercase tracking-[0.3em]">Appendix</p>
      <div className="mt-4 w-16 h-px bg-zinc-800" />
    </div>
  )
}

function SlideWhyNow() {
  return (
    <div>
      <SlideTag>Why Now</SlideTag>
      <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tighter text-white">
        A generational shift.{' '}
        <span className="text-[#FF2300]">The timing is now.</span>
      </h2>
      <div className="mt-12 grid sm:grid-cols-2 gap-6">
        <GrayBox title="A generational industry shift">Independent artists are the fastest-growing segment. Non-major labels reached 29.7% market share in 2024.</GrayBox>
        <GrayBox title="The decline of gatekeepers">DIY creators expect label-grade tools without label control. A unified career OS wasn&apos;t possible before.</GrayBox>
        <GrayBox title="Complexity is increasing">More music is released in a single day than in all of 1989. Touring and marketing are more complex than ever.</GrayBox>
        <GrayBox title="Technology readiness has converged">AI, cloud, and APIs have matured. Building a true career operating system is finally possible.</GrayBox>
      </div>
      <p className="mt-10 text-lg font-medium text-white max-w-2xl">The industry is ready for a single, modern career operating system.</p>
    </div>
  )
}

const SLIDES: Slide[] = [
  { title: 'Title', component: SlideTitle },
  { title: "What's Happening Now", component: SlideWhatsHappening },
  { title: 'Industry Trends', component: SlideIndustryTrends },
  { title: 'Current State', component: SlideProblemCurrentTools },
  { title: 'Problem', component: SlideProblemHighLevel },
  { title: 'Real Conversations', component: SlideExampleMessages },
  { title: 'Solution', component: SlideCentralizes },
  { title: 'Product', component: SlideProduct },
  { title: 'Market Size', component: SlideMarketSize },
  { title: 'Business Model', component: SlideBusinessModel },
  { title: 'Go-To-Market', component: SlideGTM },
  { title: 'Competition', component: SlideCompetition },
  { title: 'Why DIZEE Wins', component: SlideAdvantages },
  { title: 'Team', component: SlideTeam },
  { title: 'The Ask', component: SlideAsk },
  { title: 'Closing Statement', component: SlideClosingStatement },
  { title: 'Closing', component: SlideClosing },
  { title: 'Appendix', component: SlideAppendix },
  { title: 'Why Now', component: SlideWhyNow },
]
